import React, { useEffect, useState, useMemo } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
} from '@material-ui/core';
import axios from 'axios';
import { useApi, identityApiRef, discoveryApiRef } from '@backstage/core-plugin-api';

type ProjectDetail = {
  name: string;
  adoProject: string;
  adoOrg?: string;
};

/** ADO project names are stored URL-encoded; decode for display only. */
const decodeAdoProject = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

/**
 * Selects a Backstage project (`project_name`) scoped to the ADO project chosen in the
 * sibling `adoProject` field. `project_name` is the key of the SSM record at
 * /backstage/project/{project_name}; `ado_project` is a field inside that record, so the
 * two are not interchangeable.
 */
export const SelectProjectName = (props: FieldExtensionComponentProps<string>) => {
  const { onChange, formData, required, rawErrors, schema, formContext } = props;

  // Get the selected ADO project from multiple possible locations in the form context
  const resolvedAdoProject = useMemo(() => {
    const adoProject =
      formContext.formData?.adoProject ||
      (formContext as any).rootFormData?.adoProject ||
      '';

    console.log('SelectProjectName: resolved adoProject =', adoProject);
    return adoProject;
  }, [formContext]);

  const [details, setDetails] = useState<ProjectDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const identityApi = useApi(identityApiRef);
  const discoveryApi = useApi(discoveryApiRef);

  useEffect(() => {
    async function fetchProjectDetails() {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const { token } = await identityApi.getCredentials();
        const profile = await identityApi.getProfileInfo();

        const res = await axios.post(
          `${baseUrl}/ado/user/projects`,
          {
            username: profile.email,
            forceRefresh: false,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          },
        );

        const projectDetails = res.data.projectDetails as ProjectDetail[] | undefined;
        if (Array.isArray(projectDetails)) {
          setDetails(projectDetails);
          setFetchError(null);
        } else {
          console.warn('SelectProjectName: no projectDetails in /ado/user/projects response');
          setFetchError('Could not load projects. Please try again.');
        }
      } catch (err) {
        console.error('Error fetching project names:', err);
        setFetchError('Failed to fetch projects. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    fetchProjectDetails();
  }, [identityApi, discoveryApi]);

  const options = useMemo(
    () =>
      details
        .filter(d => d.adoProject === resolvedAdoProject)
        .map(d => d.name)
        .sort(),
    [details, resolvedAdoProject],
  );

  // Drop a stale selection when the ADO project changes to one that doesn't contain it.
  // Guarded on `loading` so a pre-filled value isn't wiped before the fetch resolves.
  useEffect(() => {
    if (!loading && formData && !options.includes(formData)) {
      onChange(undefined as any);
    }
  }, [loading, options, formData, onChange]);

  const helperText = !resolvedAdoProject
    ? 'Please select an ADO Project first'
    : !loading && options.length === 0
    ? `No projects found under ${decodeAdoProject(resolvedAdoProject)}`
    : null;

  return (
    <FormControl fullWidth required={required} error={!!rawErrors?.length || !!fetchError}>
      <InputLabel>{schema.title}</InputLabel>
      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Select
          value={formData || ''}
          onChange={e => onChange(e.target.value as string)}
          disabled={!resolvedAdoProject}
        >
          {options.map((option, idx) => (
            <MenuItem key={idx} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      )}
      {fetchError && <FormHelperText>{fetchError}</FormHelperText>}
      {!fetchError && helperText && <FormHelperText>{helperText}</FormHelperText>}
      {rawErrors && rawErrors.length > 0 && (
        <FormHelperText>{rawErrors.join(', ')}</FormHelperText>
      )}
    </FormControl>
  );
};
